/**
 * Actualité PRODEVAL – portage de design/components/bundle.js (NewsScene, NewsVisual, NewsPanel).
 * Points de choix de l'actualité : Radix RadioGroup (mêmes classes `ag-news-dot(-on)`) au lieu d'onglets sans
 * panneau ni flèches – un seul arrêt de tabulation, ← → ↑ ↓ Début Fin pour changer d'actualité (la sélection suit le
 * focus : useSelectionSuitFocus, liste-choix.ts).
 */
import React from 'react';
import * as RadioGroup from '@radix-ui/react-radio-group';
import { cx, h, Icon, ModuleTag, useIdStable, type Metier } from './base';
import { useSelectionSuitFocus } from './liste-choix';

export type Scene = 'biogas' | 'sunset' | 'night';

/** Illustrations en attendant les vraies photos (item.image). */
export function NewsScene(p: { kind?: Scene }) {
  const k = p.kind || 'biogas';
  const id = useIdStable('ag-ns');
  const g = (n: string) => `url(#${id}${n})`;
  const defs = h('defs', null,
    h('linearGradient', { id: id + 'sky', x1: 0, y1: 0, x2: 0, y2: 1 },
      k === 'sunset' ? [h('stop', { key: 1, offset: 0, stopColor: '#2b4a6b' }), h('stop', { key: 2, offset: 0.55, stopColor: '#e9906a' }), h('stop', { key: 3, offset: 1, stopColor: '#f6c98c' })]
        : k === 'night' ? [h('stop', { key: 1, offset: 0, stopColor: '#0b1f24' }), h('stop', { key: 2, offset: 1, stopColor: '#1c464c' })]
          : [h('stop', { key: 1, offset: 0, stopColor: '#6fa9d6' }), h('stop', { key: 2, offset: 0.7, stopColor: '#cfe4ef' }), h('stop', { key: 3, offset: 1, stopColor: '#eef4ec' })]),
    h('linearGradient', { id: id + 'dome', x1: 0, y1: 0, x2: 0, y2: 1 }, h('stop', { offset: 0, stopColor: '#8fb39b' }), h('stop', { offset: 1, stopColor: '#4f7b63' })),
    h('linearGradient', { id: id + 'tank', x1: 0, y1: 0, x2: 1, y2: 0 }, h('stop', { offset: 0, stopColor: '#9fb0b4' }), h('stop', { offset: 0.45, stopColor: '#e3eaeb' }), h('stop', { offset: 1, stopColor: '#8a9ca0' })),
    h('radialGradient', { id: id + 'sun', cx: 0.5, cy: 0.5, r: 0.5 }, h('stop', { offset: 0, stopColor: '#fff6d8', stopOpacity: 0.95 }), h('stop', { offset: 1, stopColor: '#fff6d8', stopOpacity: 0 })),
    h('filter', { id: id + 'blur' }, h('feGaussianBlur', { stdDeviation: 18 })));
  let corps: React.ReactNode[];
  if (k === 'night') {
    const points: React.ReactNode[] = [];
    for (let i = 0; i < 26; i++) points.push(h('circle', { key: i, cx: (i * 137) % 1000, cy: 120 + ((i * 89) % 520), r: 20 + ((i * 17) % 60), fill: ['#51b68f', '#81bb27', '#2f7f8a', '#e8b350'][i % 4], opacity: 0.35 }));
    corps = [h('rect', { key: 'b', width: 1000, height: 700, fill: g('sky') }), h('g', { key: 'd', filter: g('blur') }, points),
      h('path', { key: 'p', d: 'M0 560 L1000 560 L1000 700 L0 700z', fill: '#081517', opacity: 0.7 })];
  } else if (k === 'sunset') {
    corps = [h('rect', { key: 'b', width: 1000, height: 700, fill: g('sky') }), h('circle', { key: 's', cx: 700, cy: 430, r: 220, fill: g('sun') }),
      h('path', { key: 'h', d: 'M0 520 C200 500 400 530 600 510 C800 490 900 515 1000 505 L1000 700 L0 700z', fill: '#3a3550' }),
      h('g', { key: 'i', fill: '#1d2533' },
        h('rect', { x: 180, y: 360, width: 110, height: 170 }), h('rect', { x: 300, y: 300, width: 26, height: 230 }), h('rect', { x: 340, y: 400, width: 160, height: 130 }),
        h('path', { d: 'M520 530 V420 a60 60 0 0 1 120 0 V530z' }), h('rect', { x: 660, y: 250, width: 10, height: 280 }), h('rect', { x: 120, y: 470, width: 600, height: 14 }),
        h('rect', { x: 140, y: 440, width: 8, height: 90 }), h('rect', { x: 700, y: 455, width: 220, height: 75 })),
      h('circle', { key: 'f', cx: 665, cy: 244, r: 9, fill: '#ffb347' })];
  } else {
    corps = [h('rect', { key: 'b', width: 1000, height: 700, fill: g('sky') }), h('circle', { key: 's', cx: 820, cy: 150, r: 160, fill: g('sun') }),
      h('path', { key: 'h1', d: 'M0 430 C180 380 330 410 520 390 C700 370 860 400 1000 380 L1000 700 L0 700z', fill: '#a9c6a0' }),
      h('path', { key: 'h2', d: 'M0 500 C220 470 420 500 640 480 C800 465 920 485 1000 478 L1000 700 L0 700z', fill: '#7da36a' }),
      h('ellipse', { key: 'd1', cx: 330, cy: 500, rx: 170, ry: 120, fill: g('dome') }), h('rect', { key: 'w1', x: 160, y: 495, width: 340, height: 60, fill: '#d9e1e2' }),
      h('ellipse', { key: 'd2', cx: 620, cy: 515, rx: 130, ry: 92, fill: g('dome') }), h('rect', { key: 'w2', x: 490, y: 512, width: 260, height: 48, fill: '#d0d9da' }),
      h('rect', { key: 't', x: 790, y: 410, width: 90, height: 150, fill: g('tank') }), h('ellipse', { key: 'tt', cx: 835, cy: 410, rx: 45, ry: 10, fill: '#eef2f2' }),
      h('rect', { key: 'p1', x: 500, y: 540, width: 300, height: 7, fill: '#9aa8ab' }), h('rect', { key: 'st', x: 925, y: 300, width: 8, height: 260, fill: '#8d999c' }),
      h('path', { key: 'g', d: 'M0 555 L1000 555 L1000 700 L0 700z', fill: '#5f8a55' })];
  }
  return h('svg', { className: 'ag-news-scene', viewBox: '0 0 1000 700', preserveAspectRatio: 'xMidYMid slice', 'aria-hidden': true }, defs, corps);
}

export function NewsVisual(p: { image?: string; scene?: Scene }) {
  return p.image ? h('img', { src: p.image, alt: '' }) : h(NewsScene, { kind: p.scene });
}

export interface Actualite { title: string; date?: string; excerpt?: string; tag?: string; metier?: Metier; image?: string; scene?: Scene; href?: string }

export interface NewsPanelProps {
  overline: string; moreLabel: string; nextLabel?: string; index?: number; items: Actualite[];
  /** Ajout AGORA : choix de l'actualité affichée (points). */
  onIndex?: (i: number) => void;
  /**
   * Ajout AGORA : fond du panneau fourni par l'appelant, à la place du visuel de l'actualité affichée – écran de
   * connexion : diaporama de photos (`PhotoSlideshow`, diaporama.ts). Les vignettes gardent le visuel de chaque actualité.
   */
  art?: React.ReactNode;
}

export function NewsPanel(p: NewsPanelProps) {
  const items = p.items || [], idx = p.index || 0;
  const cur: Actualite = items[idx] || { title: '' };
  const autres = items.filter((_, i) => i !== idx).slice(0, 2);
  const { emettre, surFocus } = useSelectionSuitFocus(String(idx), (v) => p.onIndex?.(Number(v)));
  return h('section', { className: 'ag-news', 'aria-label': p.overline },
    h('div', { className: 'ag-news-art' }, p.art ?? h(NewsVisual, cur)),
    h('div', { className: cx('ag-news-chip', 'ag-glass') }, h('i'), p.overline),
    h('article', { className: cx('ag-news-card', 'ag-glass') },
      h('div', { className: 'ag-news-over' }, cur.date, cur.tag ? h(ModuleTag, { metier: cur.metier || 'service', label: cur.tag }) : null),
      h('h2', null, cur.title),
      cur.excerpt ? h('p', null, cur.excerpt) : null,
      h('div', { className: 'ag-news-foot' },
        h('a', { href: cur.href || '#', className: 'ag-news-more' }, p.moreLabel, h(Icon, { name: 'arrow', size: 16 })),
        // Choix exclusif de l'actualité affichée : groupe radio nommé (titre du panneau), chaque point nommé par son titre.
        items.length > 1 ? h(RadioGroup.Root, {
          className: 'ag-news-dots', 'aria-label': p.overline, value: String(idx), loop: true, onValueChange: emettre,
        }, items.map((it, i) =>
          h(RadioGroup.Item, {
            key: i, value: String(i), 'aria-label': it.title, onFocus: surFocus(String(i)),
            className: cx('ag-news-dot', i === idx && 'ag-news-dot-on'),
          }))) : null)),
    autres.length ? h('div', { className: 'ag-news-next' }, p.nextLabel ? h('div', { className: 'ag-news-next-label' }, p.nextLabel) : null, autres.map((it, i) =>
      h('a', { key: i, href: it.href || '#', className: cx('ag-news-mini', 'ag-glass') },
        h('span', { className: 'ag-news-thumb' }, h(NewsVisual, it)),
        h('span', { className: 'ag-news-mini-txt' }, h('small', null, it.date), h('b', null, it.title))))) : null);
}
